import { DataError } from "../../core/errors/DataError";
import { enviroment } from "../../enviroment";
import { DISABLED_ERROR_CODE } from "../../data/models/mappers/mapError";

// Flag REACT_APP_UPLOAD_SINGLE_REQUEST (§3C, ANALYSE-UPLOAD-HASTIGHED 13/9):
// the pure decisions of the "create + upload in ONE request" flow, kept out
// of the actions handler so they can be unit-tested without react-query,
// toasts or the router.
//
// The API's single-request route (POST organizations/{orgId}/image-logo,
// multipart) answers 404 while its own flag UploadSingleRequestEnabled is
// off - `new NotFoundResult()` (no body) in the API PR, and the repo's other
// flag-off idiom is 404 { disabled: true, flag } (mapped to errorCode
// DISABLED by errorStatusFromBody). The Functions host answers 404 without
// a body while the route is not deployed at all. All three mean "the single
// request is not available here": the upload then falls back to today's two
// requests, and the tab remembers it for ROUTE_MISSING_TTL_MS so no upload
// in the meantime pays a 404 first. A 404 WITH a message is a real answer
// from a live route ("organization not found" etc.) and is never a fallback
// - it surfaces as today's error toast.

// How long the tab remembers "the route is not available" before trying
// the single request once more. Bounded so a tab that saw a transient
// bodiless 404 from a LIVE route (a slot swap, a deploy) does not run the
// two-request flow for the rest of its life, and so an API flag turned on
// meanwhile is picked up without a reload. Cost while the API flag stays
// off: one 404 per tab per TTL window.
export const ROUTE_MISSING_TTL_MS = 15 * 60 * 1000;

// Module memory: the time until which the single-request route is known to
// be unavailable in this tab (0 = not known). Never persisted - a reload
// tries the single request again.
let routeMissingUntil = 0;

/**
 * True when the error is the route's "not available" answer: a DataError
 * whose status is 404 and whose body was either empty (errorStatusFromBody
 * maps an empty body to { code: httpStatus, message: "" } - no envelope, no
 * errorCode) or the flag-off envelope { disabled: true, flag } (errorCode
 * DISABLED - a body that can never be a real "not found"). Anything else -
 * a 404 with a message, a 409 { disabled } (another route's idiom, not this
 * one's), a 400 (admin without customerId), a 500 from app117 ("Logo size
 * is too big"), a network error - is the upload's real outcome.
 */
export function isSingleRequestRouteMissing(error: unknown): boolean {
  if (!(error instanceof DataError)) return false;
  const status = error.errorStatus;
  if (!status || status.code !== 404) return false;
  if (status.errorCode === DISABLED_ERROR_CODE) return true;
  if (status.errorCode !== undefined) return false;
  return typeof status.message !== "string" || status.message.trim() === "";
}

/** Remember for the next ROUTE_MISSING_TTL_MS that the route is not available. */
export function markSingleRequestRouteMissing(): void {
  routeMissingUntil = Date.now() + ROUTE_MISSING_TTL_MS;
}

/** Whether the tab currently remembers that the route is not available. */
export function isSingleRequestRouteMarkedMissing(): boolean {
  return Date.now() < routeMissingUntil;
}

/**
 * Whether the next upload should try the single request: the FE flag is on
 * and the tab does not currently remember that the route is missing. Flag
 * off = false without touching the memory.
 */
export function shouldTrySingleRequestUpload(): boolean {
  return enviroment.uploadSingleRequestEnabled && !isSingleRequestRouteMarkedMissing();
}

/** Tests only: forget the tab memory. */
export function resetUploadSingleRequestMemoryForTests(): void {
  routeMissingUntil = 0;
}
